import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { ApiService, CommonService, LoadingSpinnerService, WorkflowService } from '@common';

import { AccessAction, CommandResponse, EntityList, PhxConstants, UserProfile } from '@common/model';
import { AuthService } from '@common/services/auth.service';

import { environment } from '../../../environments/environment';
import { BillingTransactionGrouped, Invoice, InvoiceBillingTransaction, InvoiceExtension, InvoiceGrouped, InvoiceValidation } from '../models';

interface InvoiceCache {
  invoiceId: number;
  forceEdit: boolean;
  invoice: Invoice;
}

@Injectable()
export class InvoiceDataService {
  private FileFormatHeader: Array<string> = [
    'From',
    'Invoice Number',
    'Payee Reference',
    'Payer Reference',
    'Amount Paid',
    'Payment Date'
  ];
  private serviceApiEndpoint = environment.apiUrl;
  private invoicesList: Array<InvoiceCache> = [];

  private invoiceSubject: BehaviorSubject<Invoice | undefined> = new BehaviorSubject(null);
  invoice$: Observable<Invoice> = this.invoiceSubject.asObservable();

  private invoiceEditModeSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  invoiceEditMode$: Observable<boolean> = this.invoiceEditModeSubject.asObservable();

  private invoiceGroupedSubject: BehaviorSubject<InvoiceGrouped | undefined> = new BehaviorSubject(null);
  private billingTransactionGroupedSubject: BehaviorSubject<BillingTransactionGrouped | undefined> = new BehaviorSubject(null);


  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private commonService: CommonService,
    private workflowService: WorkflowService,
    private loadingSpinnerService: LoadingSpinnerService,
  ) { }

  private get invoice() {
    return this.invoiceSubject.value;
  }

  private set invoice(invoice: Invoice) {
    this.invoiceSubject.next(invoice);
  }

  private set invoiceEditMode(val: boolean) {
    this.invoiceEditModeSubject.next(val);
  }

  loadInvoice(id: number, isDestroyed$: Subject<boolean>, oDataParams?: any, refreshInvoice = false): Observable<InvoiceCache> {
    const foundIndex = this.invoicesList.findIndex(item => item.invoiceId === id);
    return new Observable<InvoiceCache>(observer => {
      if (foundIndex === -1 || (refreshInvoice && foundIndex > -1)) {
        this.apiQuery(id, oDataParams).pipe(distinctUntilChanged(), takeUntil(isDestroyed$)).subscribe({
          next: invoice => {
            if (invoice.OrganizationClientRoleAlternateBillId === null) {
              invoice.OrganizationClientRoleAlternateBillId = 0;
            }
            this.invoiceEditMode = this.isEditable(invoice);
            this.invoice = invoice;
            const data: InvoiceCache = {
              invoiceId: +invoice.Id,
              forceEdit: false,
              invoice
            };
            if ((refreshInvoice && foundIndex > -1)) {
              this.invoicesList[foundIndex] = data;
            } else {
              this.invoicesList.push(data);
            }

            observer.next(data);
            observer.complete();
          },
          error: error => {
            observer.error(error);
          }
        });
      } else if (foundIndex > -1) {
        const data = this.invoicesList[foundIndex];

        this.invoiceEditMode = this.isEditable(data.invoice) || data.forceEdit;
        this.invoice = data.invoice;

        observer.next(data);
        observer.complete();
      }
    });
  }

  updateInvoiceInCollection(invoice: Invoice) {
    const foundIndex = this.invoicesList.findIndex(item => item.invoiceId === invoice.Id);
    if (foundIndex > 0) {
      this.invoicesList[foundIndex].invoice = invoice;
      this.invoice = invoice;
    }
  }

  clear() {
    this.invoicesList = [];
    this.invoice = null;
    this.invoiceEditMode = null;
  }

  updateUiInvoiceEditable(id: number, editable: boolean) {
    const foundIndex = this.invoicesList.findIndex(invoice => invoice.invoiceId === id);
    if (foundIndex > -1) {
      this.invoicesList[foundIndex].forceEdit = editable;
      this.invoiceEditMode = editable;
    }
  }

  isCurrentUserHasClientRelatedRoles(): Observable<boolean> {
    return this.authService.getCurrentProfile().pipe(
      map((profile: UserProfile) => {
        const rolesIndex = profile.FunctionalRoles.findIndex(
          item => item.FunctionalRoleId === PhxConstants.FunctionalRole.OrganizationalRole
            || item.FunctionalRoleId === PhxConstants.FunctionalRole.Worker
        );
        return rolesIndex > -1;
      })
    );
  }

  isEditable(invoice: Invoice) {
    if (!invoice) {
      return false;
    } else {
      return invoice.AccessActions.some((action: AccessAction) =>
        action.AccessAction === PhxConstants.EntityAccessAction.InvoiceEdit)
        && (invoice.StatusId === PhxConstants.InvoiceStatus.Draft);
    }
  }
  validateFileFormatHeader(csvHeaders: Array<string>) {
    return this.FileFormatHeader.join(',') === csvHeaders.join(',');
  }

  csvStringToJson(csvData: string): Array<any> {
    const result = [];
    const rows: Array<string> = csvData ? csvData.split(/\r\n|\n/) : [];
    if (!rows.length) {
      return result;
    }
    const headers = rows[0].split(',');
    if (this.validateFileFormatHeader(headers)) {
      for (let i = 1; i < rows.length; i++) {
        const cells = rows[i] ? rows[i].split(',') : [];
        if (!cells.join('').trim().length) {
          continue;
        }
        if (cells.length) {
          const rowObj = {};
          for (let j = 0; j < cells.length && j < headers.length; j++) {
            rowObj[headers[j]] = cells[j];
          }
          result.push(rowObj);
        }
      }
    } else {
      this.commonService.logError('File format not supported');
    }
    // return converted JSON
    return result;
  }
  getTimesheetIdCount(csvJson: Array<any>) {
    if (csvJson?.length) {
      return new Set(csvJson.map((item) => `${item['Timesheet ID']} ${item['Work Order ID']}`)).size;
    }
    return 0;
  }
  getCommandPostFileUrl() {
    return `${this.serviceApiEndpoint}api/command/postfile`;
  }
  getCashRemittanceData(): Observable<any> {
    return this.apiService.query('Payment/GetRemittance');
  }

  getInternalProfiles(): Promise<Array<{ Id: number; Name: string }>> {
    return new Promise((resolve, reject) => {
      let result: Array<{ Id: number; Name: string }> = [];
      const filter = oreq
        .filter('ProfileStatusId')
        .eq(PhxConstants.ProfileStatus.Active)
        .or()
        .filter('ProfileStatusId')
        .eq(PhxConstants.ProfileStatus.PendingChange);

      const oDataParams = oreq
        .request()
        .withExpand(['Contact'])
        .withSelect(['Id', 'Contact/FullName'])
        .withFilter(filter)
        .withOrderby(['Contact/FullName asc'])
        .url();

      this.apiService
        .query<EntityList<any>>(`UserProfile/getListUserProfileInternal?${oDataParams}`, false)
        .subscribe({
          next: response => {
            if (response?.Items) {
              result = response.Items.map(item => {
                return { Id: item.Id, Name: item.Contact.FullName + ' - ' + item.Id };
              });
            }
            resolve(result);
          },
          error: err => {
            reject(err);
          }
        });
    });
  }

  updateBillingTransactionClearingCounts(organizationIdInternal: number) {
    this.apiService.query('BillingTransaction/grouped/internalorganization/' + organizationIdInternal).subscribe(
      (response: any) => {
        if (response?.Items?.length === 1) {
          this.billingTransactionGroupedSubject.next(response.Items[0]);
        } else {
          this.billingTransactionGroupedSubject.next({
            Count: 0,
            OrganizationIdInternal: null,
            OrganizationInternalLegalName: null,
            BillingInvoicePresentationStyles: []
          });
        }
      });
  }

  getInternalOrganizationBillingTransactionGrouped(
    organizationIdInternal: number,
    forceGet = false): Observable<BillingTransactionGrouped> {
    const targetValue = this.billingTransactionGroupedSubject.value;

    if (targetValue == null || forceGet) {
      this.updateBillingTransactionClearingCounts(organizationIdInternal);
    }
    return this.billingTransactionGroupedSubject.asObservable();
  }

  getPendingReleaseGrouped(): Observable<EntityList<InvoiceGrouped>> {
    return this.apiService.query<EntityList<InvoiceGrouped>>('invoice/pendingReleaseGrouped');
  }

  getBillingTransactionClientList(organizationIdInternal: number, invoiceTypeId: number): Observable<any> {
    return this.apiService.query(`BillingTransaction/grouped/internalorganization/${organizationIdInternal}/billinginvoicepresentationstyle/${invoiceTypeId}`);
  }

  getBillingTransactionGrouped(): Observable<EntityList<BillingTransactionGrouped>> {
    return this.apiService.query<EntityList<BillingTransactionGrouped>>('billingTransaction/grouped');
  }

  anyUnpaidBillingTransactions(invoiceId: number) {
    return lastValueFrom(this.apiService.query<boolean>(`invoice/${invoiceId}/anyUnpaidBillingTransactions`));
  }

  updateInvoiceClearingCounts(organizationIdInternal: number) {
    this.apiService.query('invoice/pendingReleaseGrouped/internalorganization/' + organizationIdInternal).subscribe(
      (response: any) => {
        if (response?.Items?.length === 1) {
          this.invoiceGroupedSubject.next(response.Items[0]);
        } else {
          this.invoiceGroupedSubject.next({
            Count: 0,
            OrganizationIdInternal: null,
            OrganizationInternalLegalName: null,
            BillingInvoicePresentationStyles: []
          });
        }
      });
  }

  getInternalOrganizationPendingReleaseGrouped(organizationIdInternal: number, forceGet: boolean = false) {
    const targetValue = this.invoiceGroupedSubject.value;

    if (targetValue == null || forceGet) {
      this.updateInvoiceClearingCounts(organizationIdInternal);
    }

    return this.invoiceGroupedSubject.asObservable();
  }

  executeInvoiceTransactionDocumentUpdateStatusCommand(
    InvoiceId: number,
    InvoiceTransactionId: number,
    InvoiceTransactionDocumentIds: number[],
    InvoiceTransactionDocumentStatusId: number,
    LastModifiedDatetime: Date,
    updateState: boolean = true
  ) {
    this.loadingSpinnerService.show();

    return new Promise((resolve, reject) => {
      this.apiService
        .command('InvoiceTransactionDocumentUpdateStatus', {
          InvoiceTransactionId,
          InvoiceTransactionDocumentIds,
          InvoiceTransactionDocumentStatusId,
          LastModifiedDatetime
        })
        .then((r: CommandResponse) => {
          if (!r.IsValid) {
            this.loadingSpinnerService.hideAll();
            reject(r.ValidationMessages);
            return;
          }

          const url = `invoice/invoiceTransactionDocuments/${r.EntityId}`;
          this.apiService
            .query(url).subscribe({
            next: (response: any) => {
              if (response?.Items && updateState) {

                const invoiceIndex = this.invoicesList.findIndex(item => item.invoiceId === InvoiceId);

                if (invoiceIndex != null && invoiceIndex !== -1) {
                  const itemIndex = this.invoicesList[invoiceIndex].invoice.InvoiceBillingTransactions.findIndex(x => x.Id === r.EntityId);
                  if (itemIndex != null && itemIndex !== -1) {
                    this.invoicesList[invoiceIndex].invoice.InvoiceBillingTransactions[itemIndex].InvoiceTransactionDocuments = response.Items;

                    if (this.invoice.Id === InvoiceId) {
                      this.invoice = this.invoicesList[invoiceIndex].invoice;
                    }
                  }
                }
              }
              this.loadingSpinnerService.hide();
              resolve(r.EntityId);
            },
            error: ex => {
                console.error(url, ex);
                this.loadingSpinnerService.hideAll();
                reject(ex);
              }
          });
        })
        .catch(ex => {
          console.error('InvoiceTransactionDocumentUpdateStatus', ex);
          this.loadingSpinnerService.hideAll();
          reject(ex);
        });
    });
  }


  executeStateCommand(commandName: string, payload: any) {
    return new Promise((resolve, reject) => {
      this.apiService
        .command(commandName, payload)
        .then((response: CommandResponse) => {
          if (!response.IsValid) {
            reject(response.ValidationMessages);
          } else {
            resolve(response);
          }
        })
        .catch(ex => {
          reject(ex);
        });
    });
  }

  executeCommand(commandName: string, payload?: any, oDataParams = null, updateState = true) {
    // show extra spinner to prevent flickering between executing action and query
    this.loadingSpinnerService.show();

    return new Promise((resolve, reject) => {
      this.apiService
        .command(commandName, payload)
        .then((r: CommandResponse) => {
          if (!r.IsValid) {
            this.loadingSpinnerService.hideAll();
            reject(r.ValidationMessages);
            return;
          }

          const entityTypeId = PhxConstants.EntityType.Invoice;
          return this.workflowService.setWatchConfigOnWorkflowEvent('/next/', entityTypeId, entityTypeId, r.EntityId).then(() => {
            const url = this.getByIdUrl(r.EntityId, oDataParams);
            this.apiService.query(url).subscribe({
              next:
                (invoice: Invoice) => {
                  if (updateState === true) {
                    this.invoiceEditMode = this.isEditable(invoice);
                    this.invoice = invoice;

                    const data: InvoiceCache = {
                      invoiceId: +invoice.Id,
                      forceEdit: false,
                      invoice
                    };
                    const invoiceIndex = this.invoicesList.findIndex(item => item.invoiceId === r.EntityId);

                    if (invoiceIndex != null && invoiceIndex !== -1) {
                      this.invoicesList[invoiceIndex] = data;
                    } else {
                      this.invoicesList.push(data);
                    }
                  }
                  this.loadingSpinnerService.hide();
                  resolve(r.EntityId);
                },
              error: ex => {
                console.error(url, ex);
                this.loadingSpinnerService.hideAll();
                reject(ex);
              }
            });
          });
        })
        .catch(ex => {
          console.error(commandName, payload, ex);
          this.loadingSpinnerService.hideAll();
          reject(ex);
        });
    });
  }

  removeInvoiceBillingTransaction(invoiceBillingTransaction: InvoiceBillingTransaction) {
    const invoiceIndex = this.invoicesList.findIndex(item => item.invoiceId === invoiceBillingTransaction.InvoiceId);

    if (invoiceIndex != null && invoiceIndex !== -1) {
      const targetInvoice = this.invoicesList[invoiceIndex].invoice;
      if (targetInvoice?.InvoiceBillingTransactions.length) {
        const billingTransactionIndex = targetInvoice.InvoiceBillingTransactions.findIndex(bt => bt.Id === invoiceBillingTransaction.Id);
        if (billingTransactionIndex !== -1) {
          targetInvoice.InvoiceBillingTransactions.splice(billingTransactionIndex, 1);
          InvoiceExtension.recalculatePropertyValuesOnTransactionRemoved(targetInvoice, invoiceBillingTransaction);
          this.invoicesList[invoiceIndex].invoice = targetInvoice;

          if (this.invoice.Id === invoiceBillingTransaction.InvoiceId) {
            this.invoice = targetInvoice;
          }
        }
      }
    }
  }

  private param(oDataParams) {
    return oDataParams ? `?${oDataParams}` : '';
  }

  private getByIdUrl(id: number, oDataParams = null): string {
    return `invoice/${id}${this.param(oDataParams)}`;
  }

  private apiQuery(id: number, oDataParams?: any, showLoader: boolean = true): Observable<any> {
    return this.apiService.query(`invoice/${id}${oDataParams ? `?${oDataParams}` : ''}`, showLoader);
  }

  getInvoiceSidePanelDetails(invoiceId: number): Observable<InvoiceValidation> {
    return this.apiService.query(`invoice/${invoiceId}/billingTransactionDetails`);
  }
}
